/**
 * Textos do reprocessamento (ADR-036) usados por mais de uma tela: o andamento, a ação, a pendência e o conjunto
 * de um pedido em palavras de quem usa. Sem dados pessoais: o conjunto mostra os filtros, não visitas.
 */

import type { PendenciaVisita } from '../services/consulta-types';
import type { ModoReprocessamento, StatusReprocessamento } from '../services/painel-types';

export const ROTULO_ANDAMENTO: Readonly<Record<StatusReprocessamento, string>> = {
  enfileirado: 'Na fila',
  em_execucao: 'Em execução',
  concluido: 'Concluído',
  falha: 'Falha',
};

export const CLASSE_ANDAMENTO: Readonly<Record<StatusReprocessamento, string>> = {
  enfileirado: 'pill pill-muted',
  em_execucao: 'pill pill-muted',
  concluido: 'pill pill-ok',
  falha: 'pill pill-err',
};

export const ROTULO_ACAO: Readonly<Record<ModoReprocessamento, string>> = {
  revalidar: 'Conferir de novo',
  reler: 'Ler a imagem de novo',
};

export const ROTULO_PENDENCIA: Readonly<Record<PendenciaVisita, string>> = {
  prompt_antigo: 'Lidas com um prompt antigo',
  regras_antigas: 'Conferidas antes da mudança de parâmetros ou bases',
};

/** Rótulos dos filtros da lista (os mesmos da tela de Visitas). */
const ROTULO_FILTRO: Readonly<Record<string, string>> = {
  q: 'Busca',
  uf: 'UF',
  escolaInep: 'INEP',
  municipioIbge: 'Município (IBGE)',
  de: 'De',
  ate: 'Até',
  resultado: 'Resultado',
  tecnico: 'Técnico',
  contratada: 'Contratada',
  imei: 'IMEI',
  iccid: 'ICCID',
  cnpj: 'CNPJ',
  pasta: 'Pasta',
  marcadores: 'Marcador',
  tipoDocumento: 'Tipo de documento',
  tipoInstalacao: 'Tipo de instalação',
  observacoes: 'Com observações',
  lote: 'Lote',
  reprocessamento: 'Reprocessamento',
  mudou: 'Só as que mudaram',
  pendencia: 'Pendência',
  // Formulário técnico (pedido avançado).
  marcador: 'Marcador',
  tipoDoc: 'Tipo de documento',
};

const FLAGS = new Set(['observacoes', 'mudou']);
const IDS = new Set(['lote', 'reprocessamento']);

function descreverFiltro(filtro: Record<string, unknown>): string | null {
  const partes = Object.entries(filtro)
    .filter(([, v]) => v !== undefined && v !== null && v !== '')
    .map(([chave, v]) => {
      const rotulo = ROTULO_FILTRO[chave] ?? chave;
      if (FLAGS.has(chave)) return rotulo;
      const valor = String(v);
      if (chave === 'pendencia') return ROTULO_PENDENCIA[valor as PendenciaVisita] ?? valor;
      return `${rotulo}: ${IDS.has(chave) ? valor.slice(0, 8) : valor}`;
    });
  return partes.length > 0 ? partes.join(' · ') : null;
}

const ehObjeto = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * O conjunto de um pedido, pelo critério que a API devolve: `{ visitaId }` (ficha), `{ pendencia }` (painel),
 * `{ filtroVisitas }` (lista) ou, no formulário técnico, `{ ids }` / o filtro antigo.
 */
export function descreverConjunto(criterio: unknown): string {
  const c = ehObjeto(criterio) ? criterio : {};
  if (typeof c.visitaId === 'string') return 'Uma visita';
  if (typeof c.pendencia === 'string') return ROTULO_PENDENCIA[c.pendencia as PendenciaVisita] ?? c.pendencia;
  if (ehObjeto(c.filtroVisitas)) return descreverFiltro(c.filtroVisitas) ?? 'Todas as visitas';
  if (Array.isArray(c.ids)) return c.ids.length === 1 ? '1 visita escolhida' : `${c.ids.length} visitas escolhidas`;
  const antigo = descreverFiltro(c);
  return antigo ? `Avançado — ${antigo}` : 'Avançado — todas as visitas';
}
